import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { products, getProductUrl } from "@/mocks/products";

const ALL_CATEGORIES = "All";
const CATEGORIES = [
  ALL_CATEGORIES,
  "Shoes",
  "Hoodies/Sweaters",
  "T-Shirts",
  "Jackets",
  "Pants/Shorts",
  "Headwear",
  "Sets",
  "Underwear/Underpants",
  "Jersey",
  "Accessories",
  "Bags",
  "Belts",
  "Perfumes",
];

const CATEGORY_ICONS: Record<string, string> = {
  All: "ri-apps-2-line",
  Shoes: "ri-footprint-line",
  "Hoodies/Sweaters": "ri-t-shirt-2-line",
  "T-Shirts": "ri-t-shirt-line",
  Jackets: "ri-archive-2-line",
  "Pants/Shorts": "ri-scissors-2-line",
  Headwear: "ri-windy-line",
  Sets: "ri-stack-line",
  "Underwear/Underpants": "ri-heart-line",
  Jersey: "ri-award-line",
  Accessories: "ri-clockwise-line",
  Bags: "ri-handbag-line",
  Belts: "ri-link-m",
  Perfumes: "ri-drop-line",
};

const CATEGORY_URLS: Record<string, string> = {
  All: "https://kaspreadsheet.com",
  Shoes: "https://kaspreadsheet.com/category-911-Shoes.html",
  "Hoodies/Sweaters": "https://kaspreadsheet.com/search-912/name/Shoes.html",
  "T-Shirts": "https://kaspreadsheet.com/search-913/name/Shoes.html",
  Jackets: "https://kaspreadsheet.com/search-914/name/Shoes.html",
  "Pants/Shorts": "https://kaspreadsheet.com/search-915/name/Shoes.html",
  Headwear: "https://kaspreadsheet.com/search-916/name/Shoes.html",
  Sets: "https://kaspreadsheet.com/search-917/name/Shoes.html",
  "Underwear/Underpants": "https://kaspreadsheet.com/search-918/name/Shoes.html",
  Jersey: "https://kaspreadsheet.com/search-919/name/Shoes.html",
  Accessories: "https://kaspreadsheet.com/search-920/name/Shoes.html",
  Bags: "https://kaspreadsheet.com/search-921/name/Shoes.html",
  Belts: "https://kaspreadsheet.com/search-922/name/Shoes.html",
  Perfumes: "https://kaspreadsheet.com/search-923/name/Shoes.html",
};

function ProductCard({ id, name, price, image }: { id: string; name: string; price: string; image: string }) {
  const shortName = name.replace(/\s*-\s*\d+$/, "");
  return (
    <a
      href={getProductUrl(id)}
      target="_blank"
      rel="noopener nofollow"
      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/50 hover:border-primary-300/60 transition-all duration-200 flex flex-col cursor-pointer"
    >
      <div className="relative aspect-square overflow-hidden bg-background-100">
        <img
          src={image}
          alt={shortName}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => { (e.currentTarget as HTMLImageElement).src = "https://readdy.ai/api/search-image?query=Clean%20white%20product%20photography%20studio%20background%20soft%20diffused%20lighting%20minimal%20neutral%20aesthetic&width=400&height=400&seq=placeholder-prod-001&orientation=squarish"; }}
        />
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="bg-primary-500 text-background-50 text-xs font-semibold px-2.5 py-1 rounded-md whitespace-nowrap">
            View &rarr;
          </span>
        </div>
      </div>
      <div className="p-3 md:p-4 flex flex-col flex-1">
        <p className="text-xs md:text-sm text-foreground-800 font-medium leading-snug line-clamp-2 mb-2 group-hover:text-primary-700 transition-colors flex-1">
          {shortName}
        </p>
        <div className="flex items-center justify-between mt-1">
          <span className="text-sm font-bold text-primary-600">{price}</span>
          <span className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded">
            #{id}
          </span>
        </div>
      </div>
    </a>
  );
}

export default function ProductsPage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(ALL_CATEGORIES);
  const [searchQuery, setSearchQuery] = useState("");

  const filtered = useMemo(() => {
    let result = products;
    if (activeCategory !== ALL_CATEGORIES) {
      result = result.filter((p) => p.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((p) => p.name.toLowerCase().includes(q));
    }
    return result;
  }, [activeCategory, searchQuery]);

  const browseCategoryUrl = activeCategory === ALL_CATEGORIES
    ? "https://kaspreadsheet.com"
    : CATEGORY_URLS[activeCategory] || "https://kaspreadsheet.com";

  return (
    <div className="min-h-screen bg-background-50 font-body">
      <Navbar />

      <div className="pt-16 md:pt-20">
        {/* Header Banner */}
        <section className="relative bg-background-100 border-b border-background-200/50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-6xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
              <a href="/" className="hover:text-primary-600 transition-colors">{t("breadcrumbs.home")}</a>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700 font-medium">{t("products.page_title")}</span>
            </nav>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-2">
                  {t("products.page_title")}
                </h1>
                <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
                  {t("products.page_subtitle")}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="bg-primary-500 text-background-50 px-4 py-2 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  CSSBuy Spreadsheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="bg-accent-500 text-background-50 px-4 py-2 rounded-md text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Search + Filter Bar */}
        <section className="sticky top-[64px] md:top-[72px] z-40 bg-background-50 border-b border-background-200/50">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-6xl mx-auto py-3 md:py-4">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative flex-1 max-w-sm">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("products.search_placeholder")}
                  className="w-full pl-9 pr-4 py-2 text-sm bg-background-100 border border-background-200/60 rounded-md text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-1 focus:ring-primary-400 focus:border-primary-400 transition"
                />
              </div>

              {/* Category filter pills */}
              <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 flex-wrap sm:flex-nowrap">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${
                      activeCategory === cat
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200/60"
                    }`}
                  >
                    <i className={`${CATEGORY_ICONS[cat] || "ri-price-tag-3-line"} text-sm`}></i>
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Results Info + Browse Full Category CTA */}
        <section className="w-full px-4 md:px-6 lg:px-10 max-w-6xl mx-auto pt-5 pb-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-sm text-foreground-600">
            {filtered.length} {t("products.results_count")}
            {activeCategory !== ALL_CATEGORIES && (
              <span className="ml-1 text-foreground-800 font-medium">— {activeCategory}</span>
            )}
          </p>
          <a
            href={browseCategoryUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-1.5 text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-external-link-line"></i>
            {t("products.browse_full_category")}
          </a>
        </section>

        {/* Product Grid */}
        <section className="w-full px-4 md:px-6 lg:px-10 max-w-6xl mx-auto pb-16">
          {filtered.length === 0 ? (
            <div className="py-20 text-center">
              <i className="ri-search-line text-4xl text-foreground-300 mb-4 block"></i>
              <p className="text-foreground-500 text-base">{t("products.no_results")}</p>
              <button
                onClick={() => { setSearchQuery(""); setActiveCategory(ALL_CATEGORIES); }}
                className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
              >
                {t("products.clear_filters")}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
              {filtered.map((product) => (
                <ProductCard
                  key={product.id}
                  id={product.id}
                  name={product.name}
                  price={product.price}
                  image={product.image}
                />
              ))}
            </div>
          )}
        </section>

        {/* Bottom CTA */}
        <section className="py-12 bg-primary-500">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-4xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-background-50 mb-3">
              {t("products.cta_title")}
            </h2>
            <p className="text-sm md:text-base text-background-50/85 mb-6 max-w-xl mx-auto">
              {t("products.cta_desc")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="bg-background-50 text-primary-700 px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                CSSBuy Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="border border-background-50/40 text-background-50 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}